define([
  'react',
  'prop-types',
  'immutable',
  'underscore',
  'es6!sumo/util',
  'jsx!launcher/views/listbuilder/wysiwyg/components/Component',
  'jsx!launcher/views/listbuilder/wysiwyg/components/Autocomplete',
  'es6!launcher/util/listbuilder/suggestions/index',
  'es6!launcher/util/listbuilder/constants',
], (React, PropTypes, Immutable, _, Util, Component, Autocomplete, Suggestions, Constants) => {
  // Key character codes
  const TAB = 9;
  const ENTER = 13;
  const ESC = 27;
  const UP = 38;
  const DOWN = 40;
  const INPUT_HEIGHT = 52;
  const INPUT_MARGIN = 20;

  const SUGGESTION_ENGINES = {
    'email': Suggestions.Email,
    // e.g. 'country': Suggestions.Country,
  };

  const { FormTypes } = Constants;

  const VALID_STYLE_ATTRS = [
    'color',
    'background',
    'backgroundColor',
    'fontFamily',
    'fontWeight',
    'fontStyle',
    'fontSize',
    'textAlign',
    'borderTopWidth',
    'borderLeftWidth',
    'borderBottomWidth',
    'borderRightWidth',
    'borderColor',
    'borderStyle',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'boxShadow',
    'textShadow',
  ];

  @Component()
  class TextField extends React.Component {
    static propTypes = {
      placeholder: PropTypes.string,
      style: PropTypes.object,
      onSaveInputField: PropTypes.func,
      disabled: PropTypes.bool,
    };

    static defaultProps = {
      style: Immutable.Map(),
      disabled: false,
    }

    constructor(props) {
      super(props);

      const { formType } = props;
      const inputHeight = formType === FormTypes.SMART_BAR ? Math.max(props.maxHeight - INPUT_MARGIN, 32) : 52;

      this.state = {
        value: '',
        autocompleteComponent: null,
        autocompleteIndex: null,
        suggestions: [],
        spellingSuggestions: [],
        ignoreBlur: false,
        inputHeight: inputHeight || INPUT_HEIGHT,
        scrollLeft: 0,
      };

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onFocus = this.onFocus.bind(this);
      this.onBlur = this.onBlur.bind(this);

      this.onSetIgnoreBlur = this.onSetIgnoreBlur.bind(this);
      this.onHoverSuggestion = this.onHoverSuggestion.bind(this);
      this.onCloseAutocomplete = this.onCloseAutocomplete.bind(this);
      this.onSaveAutocomplete = this.onSaveAutocomplete.bind(this);
      this.getAllSuggestions = this.getAllSuggestions.bind(this);
    }

    onClick() {
      const { suggestions, autocompleteIndex, value, autocompleteComponent } = this.state;
      if (!autocompleteComponent) return;
      const safeIndex = autocompleteIndex >= suggestions.length ? suggestions.length - 1 : autocompleteIndex;
      this.onCloseAutocomplete(suggestions[safeIndex || 0] || value);
    }

    onKeyDown(e) {
      const keyCode = e.which || e.keyCode || 0;
      const { onEnterSubmit, onBlur } = this.props;
      const { autocompleteComponent, autocompleteIndex } = this.state;

      if (!autocompleteComponent) return;

      const selection = this.getAllSuggestions()[autocompleteIndex || 0];

      switch (keyCode) {
        case UP:
        case DOWN: {
          this.navigateSuggestions(keyCode === UP ? -1 : 1);
          e.preventDefault();
          break;
        }
        case TAB: {
          if (selection) this.onCloseAutocomplete(selection);
          break;
        }
        case ENTER: {
          // Prevent any possibility of page reload on default submit action
          e.preventDefault();
          if (selection) {
            this.onCloseAutocomplete(selection);
          } else if (onEnterSubmit) {
            onEnterSubmit();
          }
          break;
        }
        case ESC: {
          if (this.isAutocompleteOpen()) {
            this.onCloseAutocomplete();
          } else if (onBlur) {
            onBlur();
          }
          break;
        }
      }
    }

    onChange(e) {
      const { name, onChangeForm } = this.props;
      const newValue = e.currentTarget.value;

      if (onChangeForm) onChangeForm({ [name]: newValue });
      this.setState({ value: newValue, scrollLeft: e.currentTarget.scrollLeft }, function() {
        this.loadSuggestions();
      });
    }

    onFocus(e) {
      const { onFocus } = this.props;

      this.loadSuggestions();
      if (onFocus) onFocus();
    }

    onBlur(e) {
      const { onBlur } = this.props;
      const { autocompleteComponent, ignoreBlur } = this.state;

      if (autocompleteComponent && !ignoreBlur) {
        autocompleteComponent.onClose();
      }

      if (onBlur) onBlur();
    }

    onHoverSuggestion(index) {
      this.setState({ autocompleteIndex: index });
    }

    loadSuggestions() {
      const { name } = this.props;
      const { autocompleteComponent, value } = this.state;
      if (!autocompleteComponent) return;

      const suggestionEngine = SUGGESTION_ENGINES[name];
      if (!suggestionEngine) return;

      suggestionEngine.suggests(value, (suggestions, spellingSuggestions) => {
        this.setState({
          suggestions: suggestions,
          spellingSuggestions: spellingSuggestions,
        }, function() {

          // Requires updated state value
          if (!this.isAutocompleteOpen()) {
            this.onCloseAutocomplete(null);
          }
        });
      });
    }

    navigateSuggestions(direction) {
      const { autocompleteIndex } = this.state;
      const allSuggestions = this.getAllSuggestions();
      if (!allSuggestions.length) return;

      let newIndex = autocompleteIndex;
      if (newIndex === null) {
        newIndex = direction === 1 ? 0 : allSuggestions.length - 1;
      } else {
        newIndex += direction;
      }

      if (newIndex < 0) newIndex = allSuggestions.length - 1;
      if (newIndex === allSuggestions.length) newIndex = 0;

      this.setState({ autocompleteIndex: newIndex });
    }

    onSetIgnoreBlur() {
      this.setState({ ignoreBlur: true });
    }

    isAutocompleteOpen() {
      return this.getAllSuggestions().length > 0;
    }

    onSaveAutocomplete(autocomplete) {
      if (!autocomplete) return;

      this.setState({
        autocompleteComponent: autocomplete,
      });
    }

    onCloseAutocomplete(suggestion) {
      const { value } = this.state;
      const { name, onChangeForm } = this.props;

      this.setState({
        suggestions: [],
        spellingSuggestions: [],
        ignoreBlur: false,
      });

      if (suggestion && onChangeForm) {
        this.setState({ value: suggestion });
        onChangeForm({ [name]: suggestion });
      }
    }

    getAllSuggestions() {
      const { suggestions, spellingSuggestions } = this.state;

      return suggestions.concat(spellingSuggestions);
    }

    renderAutocomplete(inputStyle) {
      const {
        suggestions,
        spellingSuggestions,
        autocompleteIndex,
        value,
        inputHeight,
        scrollLeft,
      } = this.state;
      const { containerStyle, formType, position } = this.props;

      const allowedStyles = [
        'fontSize',
        'fontStyle',
        'fontFamily',
        'fontWeight',
        'padding',
        'margin',
        'height',
        'width',
        'backgroundColor',
        'textAlign',
        'borderTopWidth',
        'borderLeftWidth',
        'borderBottomWidth',
        'borderRightWidth',
        'borderColor',
        'borderStyle',
        'borderTopLeftRadius',
        'borderTopRightRadius',
        'borderBottomLeftRadius',
        'borderBottomRightRadius',
      ];

      let showAboveInput = false;

      if (formType === FormTypes.SMART_BAR) {
        allowedStyles.push('opacity');

        if (position === 'bottom') {
          showAboveInput = true;
        }
      }

      inputStyle = _.extend({}, {
        position: 'absolute',
        top: '0',
        color: '#999',
        zIndex: isNaN(inputStyle.zIndex) ? 0 : inputStyle.zIndex - 1,
      }, _.pick(
        inputStyle,
        allowedStyles
      ));

      return (
        <Autocomplete
          ref={this.onSaveAutocomplete}
          inputStyle={inputStyle}
          scrollLeft={scrollLeft}
          selectedIndex={autocompleteIndex}
          value={value}
          containerStyle={containerStyle}
          suggestions={suggestions}
          showAboveInput={showAboveInput}
          spellingSuggestions={spellingSuggestions}
          getAllSuggestions={this.getAllSuggestions}
          onSetIgnoreBlur={this.onSetIgnoreBlur}
          onCloseAutocomplete={this.onCloseAutocomplete}
          onHoverSuggestion={this.onHoverSuggestion}
        />
      );
    }

    /**
     * Injects placeholder pseudo declarations into DOM if needed
     */
    renderPlaceholderPseudoIfNeeded() {
      const { name, placeholderColor } = this.props;
      const selector = `.sumome-react-wysiwyg-welcome-mat-textField input[name='${name}']`;
      let placeholderColorPseudo = null;

      if (placeholderColor) {
        const declaration = `{ color: ${placeholderColor} !important; }`;
        const declarations = [
          `${selector}::placeholder ${declaration}`,
          `${selector}::-webkit-input-placeholder  ${declaration}`,
          `${selector}:-ms-input-placeholder ${declaration}`,
          `${selector}::-ms-input-placeholder ${declaration}`,
        ].join('\n');

        placeholderColorPseudo = (
          <style dangerouslySetInnerHTML={{ __html: declarations }} />
        );
      }

      return placeholderColorPseudo;
    }

    render() {
      const {
        name,
        placeholder,
        isPreview,
        isEmbedded,
        onSaveInputField,
        onMouseEnter,
        onMouseLeave,
        formType,
        maxHeight,
        disabled,
      } = this.props;

      const { value, suggestions, spellingSuggestions } = this.state;

      const style = _.pick(this.props.style.toJS(), VALID_STYLE_ATTRS);
      // Add a little padding so the text isn't sitting right on the side.
      style.padding = '0 14px';
      style.margin = '0';
      style.display = 'block';
      style.width = '100%';
      style.maxHeight = `${INPUT_HEIGHT}px`;
      style.height = 'auto';
      style.transition = 'none';

      if (formType === FormTypes.SMART_BAR) {
        style.height = `${Math.max(maxHeight - INPUT_MARGIN, 32)}px`;
        style.maxHeight = '';
      }

      const parsedHeight = parseInt(this.props.style.get('height'), 10);
      if (!isNaN(parsedHeight) && formType !== FormTypes.SMART_BAR) {
        style.height = this.props.style.get('height');

        if (parsedHeight > INPUT_HEIGHT) {
          style.maxHeight = 'none';
        }
      }

      // Create copy of input styles for autocomplete
      const autocompleteStyle = _.extend({ opacity: 0.6 }, style);

      if (suggestions.length && style.textAlign !== 'right' && formType !== FormTypes.SMART_BAR) {
        style.backgroundColor = 'transparent';
        style.position = 'absolute';
        style.zIndex = style.zIndex || 'initial';
      }

      const autoFocus = !isEmbedded && formType !== FormTypes.SMART_BAR && name === 'email' && !Util.isMobile();

      return (
        <div>
          {this.renderAutocomplete(autocompleteStyle)}
          <input
            type="text"
            ref={onSaveInputField}
            value={value}
            name={name}
            style={style}
            spellCheck={false}
            placeholder={placeholder}
            disabled={isPreview || disabled}
            onChange={this.onChange}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onKeyDown={this.onKeyDown}
            autoFocus={autoFocus}
            onClick={this.onClick}
            autoComplete="off"
          />
          {this.renderPlaceholderPseudoIfNeeded()}
        </div>
      );
    }
  }

  return TextField;
});
